import { getApiClient } from "@punchup/utils";
import { logException } from "@punchup/sentry";

import {
  AUTOMATIONS_ENTITLEMENT_STATUSES,
  type AutomationsEntitlementResponse,
  type AutomationsSubscriptionAction,
  type AutomationsUpgradePreview,
} from "../../models/automations-entitlement";
import type { AutomationsBilling } from "../../models/automations-billing";
import type { AutomationsCheckoutSurface } from "../../models/automations-checkout-return";
import type { AutomationsCustomRequestInput } from "../../models/automations-custom-request";
import type {
  AutomationsBillingInterval,
  AutomationsPlanKey,
  AutomationsTier,
} from "../../models/automations-plans";
import type { AutomationsUsage } from "../../models/automations-usage";

const BASE = "/api/v2/manager/automations";

interface RequestOptions {
  query?: Record<string, string>;
  body?: object;
  /** Reports a failure to Sentry; omitted where the caller surfaces the error itself. */
  logAs?: { source: string; extra: Record<string, unknown> };
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

async function request<T>(
  method: "get" | "post",
  path: string,
  opts: RequestOptions & { optional: true }
): Promise<T | undefined>;
async function request<T>(
  method: "get" | "post",
  path: string,
  opts?: RequestOptions
): Promise<T>;
async function request<T>(
  method: "get" | "post",
  path: string,
  { query, body, logAs, optional }: RequestOptions & { optional?: boolean } = {}
): Promise<T | undefined> {
  const queryString = query
    ? `?${Object.entries(query)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&")}`
    : "";
  const url = `${BASE}/${path}${queryString}`;
  try {
    const client = getApiClient();
    const res =
      method === "get"
        ? await client.get<{ data?: T }>(url)
        : await client.post<{ data?: T }>(url, body);
    if (res.data === undefined && !optional) {
      throw new Error(`Malformed ${path} response`);
    }
    return res.data;
  } catch (error) {
    if (logAs) {
      logException(toError(error), {
        extra: logAs.extra,
        tags: { source: logAs.source },
      });
    }
    throw error;
  }
}

export async function fetchAutomationsEntitlement(
  artistId: string
): Promise<AutomationsEntitlementResponse> {
  const data = await request<{ entitlement?: AutomationsEntitlementResponse }>(
    "get",
    "entitlement",
    { query: { artistId }, optional: true }
  );
  const entitlement = data?.entitlement;
  if (
    !entitlement ||
    !AUTOMATIONS_ENTITLEMENT_STATUSES.includes(entitlement.status)
  ) {
    const error = new Error("Malformed automations entitlement response");
    logException(error, { extra: { artistId } });
    throw error;
  }
  return entitlement;
}

export const fetchAutomationsBilling = (
  artistId: string
): Promise<AutomationsBilling> =>
  request("get", "billing", { query: { artistId } });

export const fetchAutomationsUsage = (
  artistId: string,
  since: string
): Promise<AutomationsUsage> =>
  request("get", "usage", { query: { artistId, since } });

export interface AutomationsCheckoutSessionInput {
  artistId: string;
  tier: AutomationsTier;
  interval: AutomationsBillingInterval;
  /** Dashboard path to land on after checkout; the server re-validates it. */
  returnTo: string;
  /** Which checkout opened the session; the server falls back to its home. */
  surface: AutomationsCheckoutSurface;
}

export type AutomationsCheckoutSessionResult =
  | { kind: "session"; clientSecret: string; sessionId: string }
  | { kind: "existing" };

/** The server resolves the Stripe price ids; the client never sends a price. */
export async function fetchAutomationsCheckoutSession(
  input: AutomationsCheckoutSessionInput
): Promise<AutomationsCheckoutSessionResult> {
  const data = await request<{
    clientSecret?: string;
    sessionId?: string;
    existingSubscription?: unknown;
  }>("post", "checkout-session", {
    body: input,
    logAs: {
      source: "fetchAutomationsCheckoutSession",
      extra: {
        artistId: input.artistId,
        tier: input.tier,
        interval: input.interval,
        surface: input.surface,
      },
    },
  });
  if (data.existingSubscription) return { kind: "existing" };
  if (!data.clientSecret || !data.sessionId) {
    throw new Error("Malformed checkout-session response");
  }
  return {
    kind: "session",
    clientSecret: data.clientSecret,
    sessionId: data.sessionId,
  };
}

export interface AutomationsCheckoutOverageInput {
  artistId: string;
  sessionId: string;
  overageEnabled: boolean;
  overageCapCents: number;
}

/** Writes the overage choice onto the open checkout session before the card is charged. */
export async function saveAutomationsCheckoutOverage(
  input: AutomationsCheckoutOverageInput
): Promise<void> {
  await request("post", "checkout-session/overage", {
    body: input,
    optional: true,
    logAs: {
      source: "saveAutomationsCheckoutOverage",
      extra: { artistId: input.artistId, sessionId: input.sessionId },
    },
  });
}

export interface AutomationsCheckoutPlanInput extends AutomationsPlanKey {
  artistId: string;
  sessionId: string;
}

/** Switches the open checkout session to the plan on screen, keeping its payment form mounted. */
export async function updateAutomationsCheckoutPlan(
  input: AutomationsCheckoutPlanInput
): Promise<void> {
  await request("post", "checkout-session/plan", {
    body: input,
    optional: true,
    logAs: {
      source: "updateAutomationsCheckoutPlan",
      extra: {
        artistId: input.artistId,
        sessionId: input.sessionId,
        tier: input.tier,
        interval: input.interval,
      },
    },
  });
}

export async function submitAutomationsCustomRequest(
  input: AutomationsCustomRequestInput
): Promise<void> {
  await request("post", "custom-request", {
    body: input,
    optional: true,
    logAs: {
      source: "submitAutomationsCustomRequest",
      extra: { artistId: input.artistId },
    },
  });
}

export interface AutomationsOverageInput {
  overageEnabled: boolean;
  /** Omitted leaves the saved limit as it is; the server clamps to $10–$5,000. */
  overageCapCents?: number;
}

export async function updateAutomationsOverage(
  artistId: string,
  input: AutomationsOverageInput
): Promise<AutomationsEntitlementResponse> {
  const data = await request<{ entitlement?: AutomationsEntitlementResponse }>(
    "post",
    "overage",
    { body: { artistId, ...input }, optional: true }
  );
  if (!data?.entitlement) {
    throw new Error("Malformed automations overage response");
  }
  return data.entitlement;
}

/** A SetupIntent on the subscription's customer; its secret mounts the card form. */
export async function createAutomationsSetupIntent(
  artistId: string
): Promise<string> {
  const data = await request<{ clientSecret?: string }>(
    "post",
    "payment-method",
    { body: { artistId, action: "setup" }, optional: true }
  );
  if (!data?.clientSecret) throw new Error("Malformed setup response");
  return data.clientSecret;
}

/** ISO date of the invoice paid on save, or null when nothing was retried. */
export async function saveAutomationsPaymentMethod(
  artistId: string,
  setupIntentId: string
): Promise<{ retriedInvoiceDate: string | null }> {
  const data = await request<{ retriedInvoiceDate: string | null }>(
    "post",
    "payment-method",
    { body: { artistId, action: "confirm", setupIntentId }, optional: true }
  );
  return { retriedInvoiceDate: data?.retriedInvoiceDate ?? null };
}

const subscriptionPost = <T>(
  artistId: string,
  action: AutomationsSubscriptionAction,
  target?: AutomationsPlanKey
) =>
  request<T>("post", "subscription", {
    body: { artistId, action, ...target },
  });

const subscriptionEntitlement = async (
  artistId: string,
  action: AutomationsSubscriptionAction,
  target?: AutomationsPlanKey
) =>
  (
    await subscriptionPost<{ entitlement: AutomationsEntitlementResponse }>(
      artistId,
      action,
      target
    )
  ).entitlement;

export const previewAutomationsChange = (
  artistId: string,
  target: AutomationsPlanKey
) =>
  subscriptionPost<AutomationsUpgradePreview>(
    artistId,
    "preview_change",
    target
  );

export const changeAutomationsPlan = (
  artistId: string,
  target: AutomationsPlanKey
) => subscriptionEntitlement(artistId, "change", target);

export const cancelAutomationsPlan = (artistId: string) =>
  subscriptionEntitlement(artistId, "cancel");

export const resumeAutomationsPlan = (artistId: string) =>
  subscriptionEntitlement(artistId, "resume");
